'use client';
import { logOut } from '@/store/slices/authSlice';
import { clearBrand, clearSte } from '@/store/slices/steSlice';
import { IdToken } from '@/types';
import { Brand } from '@/types/Brand';
import NoHashQueryStringUtils from '@/utils/NoHashQueryStringUtils';
import getEnv from '@/utils/env';
import {
  AuthorizationNotifier,
  AuthorizationRequest,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  DefaultCrypto,
  FetchRequestor,
  LocalStorageBackend,
  RedirectRequestHandler,
  RevokeTokenRequest,
  TokenRequest,
} from '@openid/appauth';
import { jwtDecode } from 'jwt-decode';
import log from 'loglevel';

const getBrand = (brand: Brand): string => {
  switch (brand) {
    case 'cetelem':
      return 'PF';
    case 'domofinance':
      return 'DOMO';
    case 'cafineo':
      return 'CM';
    case 'cmoi':
      return 'CM';
    case 'cmag':
      return 'CM';
    default:
      return 'PF';
  }
};

const getStore = () => {
  let store = {};
  try {
    store = JSON.parse(sessionStorage.getItem('persist:root') ?? '{}');
    let storeCopy = {};
    Object.keys(store ?? {}).forEach((key) => {
      // @ts-ignore
      storeCopy[key] = JSON.parse(store[key]);
    });
    store = storeCopy;
  } catch (e) {
    console.error('Could not retrieve store => ', e);
  }
  return store;
};

const getRedirectUri = (brand?: Brand) =>
  `${
    window.origin +
    window.location.pathname.split('/').slice(0, -2).join('/') +
    '/' +
    // @ts-ignore
    (brand ?? getStore()?.selectedSte?.brand ?? 'cetelem')
  }${'/callback'}`;

export const authorize = (brand: Brand) => {
  const openIdConnectUrl = getEnv().openIdConnectUrl;
  const clientId = getEnv().openIdClientId;
  const profile = getEnv().PROFIL;

  const authorizationHandler = new RedirectRequestHandler();

  function getScope() {
    if (profile == 'PF') return ' appartenance';
    return '';
  }

  AuthorizationServiceConfiguration.fetchFromIssuer(
    openIdConnectUrl,
    new FetchRequestor(),
  )
    .then((response) => {
      log.info('Fetched service configuration', response);
      log.info('Completed fetching configuration');

      const authRequest = new AuthorizationRequest({
        client_id: clientId,
        redirect_uri: getRedirectUri(brand),
        scope: 'openid profile' + getScope(),
        response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
        state: '',
        extras: {
          finEnt: getBrand(brand),
        },
      });

      authorizationHandler.performAuthorizationRequest(response, authRequest);
    })
    .catch((error) => {
      log.info('Something bad happened', error);
      log.info(`Something bad happened ${error}`);
    });
};

export function callback() {
  return new Promise((resolve: (userInfo: IdToken) => void, reject) => {
    const openIdConnectUrl = getEnv().openIdConnectUrl;
    const clientId = getEnv().openIdClientId;
    const authorizationHandler = new RedirectRequestHandler(
      new LocalStorageBackend(),
      new NoHashQueryStringUtils(),
      window.location,
      new DefaultCrypto(),
    );
    const notifier = new AuthorizationNotifier();
    authorizationHandler.setAuthorizationNotifier(notifier);
    const tokenHandler = new BaseTokenRequestHandler(new FetchRequestor());
    notifier.setAuthorizationListener((request, response, error) => {
      log.info(1, request, 2, response, 3, error);
      if (response) {
        let extras: any = {};
        if (request && request.internal) {
          extras.code_verifier = request.internal.code_verifier;
        }
        let tokenRequest = new TokenRequest({
          client_id: clientId,
          redirect_uri: getRedirectUri(),
          grant_type: 'authorization_code',
          code: response.code,
          extras: extras,
        });

        AuthorizationServiceConfiguration.fetchFromIssuer(
          openIdConnectUrl,
          new FetchRequestor(),
        )
          .then((configuration) => {
            log.info(configuration, tokenRequest);
            return tokenHandler.performTokenRequest(
              configuration,
              tokenRequest,
            );
          })
          .then((res) => {
            sessionStorage.setItem('accessToken', res.accessToken);
            const expiresAt = (res.issuedAt + (res.expiresIn || 0)).toString();
            sessionStorage.setItem('expiresAt', expiresAt);
            sessionStorage.setItem('refreshToken', res.refreshToken || '');
            if (res.idToken) {
              const userInfo = jwtDecode<IdToken>(res.idToken);
              sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
              resolve(userInfo);
            }
          })
          .catch((e) => log.info(e));
      }
    });
    authorizationHandler
      .completeAuthorizationRequestIfPossible()
      .then(() => log.info('Completing authentication if possible'))
      .catch((error) => {
        log.info('error', error);
      });
  });
}

export function refreshToken() {
  return new Promise<string>((resolve, reject) => {
    const refreshToken = sessionStorage.getItem('refreshToken');
    if (!refreshToken) {
      resolve('');
      return;
    }
    const openIdConnectUrl = getEnv().openIdConnectUrl;
    const clientId = getEnv().openIdClientId;
    const tokenHandler = new BaseTokenRequestHandler(new FetchRequestor());
    AuthorizationServiceConfiguration.fetchFromIssuer(
      openIdConnectUrl,
      new FetchRequestor(),
    )
      .then((configuration) => {
        let tokenRequest = new TokenRequest({
          client_id: clientId,
          redirect_uri: getRedirectUri(),
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
        });
        return tokenHandler.performTokenRequest(configuration, tokenRequest);
      })
      .then((res) => {
        sessionStorage.setItem('accessToken', res.accessToken);
        const expiresAt = (res.issuedAt + (res.expiresIn || 0)).toString();
        sessionStorage.setItem('expiresAt', expiresAt);
        sessionStorage.setItem('refreshToken', res.refreshToken || '');
        resolve(res.accessToken);
      })
      .catch((e) => {
        log.info(e);
        reject();
      });
  });
}

export const disconnect = () => {
  return new Promise<void>((resolve, reject) => {
    const openIdConnectUrl = getEnv().openIdConnectUrl;
    const clientId = getEnv().openIdClientId;
    AuthorizationServiceConfiguration.fetchFromIssuer(
      openIdConnectUrl,
      new FetchRequestor(),
    ).then((response) => {
      const revokeTokenRequest = new RevokeTokenRequest({
        token:
          typeof window !== 'undefined'
            ? sessionStorage.getItem('accessToken') || ''
            : '',
        client_id: clientId,
      });
      new BaseTokenRequestHandler(new FetchRequestor())
        .performRevokeTokenRequest(response, revokeTokenRequest)
        .then(() => {
          log.info('disconnected');
          resolve();
        })
        .catch((e) => reject(e));
    });
  });
};

export const removeCredentials = (dispatch: any) => {
  sessionStorage.removeItem('accessToken');
  sessionStorage.removeItem('userInfo');
  dispatch(clearSte());
  dispatch(logOut());
  dispatch(clearBrand());
};
